/* eslint-disable @typescript-eslint/indent */

// Core
import styled, { css } from 'styled-components';

// Components
import { StatusIndicatorStatus } from '../../../../../../layout/elements/StatusIndicator';

// Config
import { BREAKPOINTS } from '../../../../../../config/screen';

// Types
import { Theme } from '../../../../../../types/theme';

/** Reusable styles */
const borderColor = (
  { theme }: { theme: Theme },
) => theme.color.crusherDashboard.hydraulicUnitDrawer.borderColor;

const flexBetween = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ExecuteCommandButtonContainer = styled.div`
  margin-top: auto;
`;

export const OverallStatusContainer = styled.div`
  width: 100%;
  padding: 0.75rem 1rem;
  ${flexBetween};
  border-bottom: 1px solid ${borderColor};
  box-sizing: border-box;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 1rem 1.5rem;
  };

  @media (min-width: ${BREAKPOINTS.xl}px) {
    border-right: 1px solid ${borderColor};
  };
`;

export const TankTempContainer = styled.div`
  padding: 0.75rem 1rem;
  ${flexBetween};
  border-bottom: 1px solid ${borderColor};
  box-sizing: border-box;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 1rem 1.5rem;
  };

  @media (min-width: ${BREAKPOINTS.xl}px) {
    width: calc(100% + 2px);
  };
`;

export const CurrentCssContainer = styled.div`
  ${flexBetween};
  border-radius: 4px;
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid ${borderColor};
  min-height: 4.25rem;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 1.5rem 1.25rem;
  };

  @media (min-width: ${BREAKPOINTS.md}px) {
    padding: 1.5rem 1.25rem;
  };
`;

export const CSSettingContainer = styled.div`
  padding: 0.75rem 1rem;
  // border-right: 1px solid ${borderColor}; // @TODO: Add this line back when add controls
  height: 100%;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 1rem 1.5rem;
  };

  @media (min-width: ${BREAKPOINTS.md}px) {
    padding: 1rem 1.5rem;
  };
`;

export const TargetValueContainer = styled.div`
  ${flexBetween};
  border-radius: 4px;
  margin: 0.5rem 0 1rem;
  padding: 0.875rem 1.25rem;
  border: 1px solid ${borderColor};
  min-height: 4.25rem;
  gap: 8px;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 1rem 1.25rem;
  };
`;

export const ParamIncrementorContainer = styled.div`
  box-sizing: border-box;
  flex: 1;
`;

export const ClampSpeedometerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 0 1.25rem;
  border-bottom: 1px solid ${borderColor};

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 1.5rem 0 2rem;
  };

  @media (min-width: ${BREAKPOINTS.md}px) {
    padding: 2rem 0 2.75rem;
  };

  @media (min-width: ${BREAKPOINTS.xl}px) {
    border-bottom: none;
    border-right: 1px solid ${borderColor};
    padding: 1.8125rem 0 2.75rem;
  };
`;

export const TrampSpeedometerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 0 1.25rem;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 1.5rem 0 2rem;
  };

  @media (min-width: ${BREAKPOINTS.md}px) {
    padding: 2rem 0 2.75rem;
  };

  @media (min-width: ${BREAKPOINTS.xl}px) {
    padding: 1.8125rem 0 2.75rem;
  };
`;

export const NumberTeethContainer = styled.div`
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 9px;
  justify-content: center;
  align-items: center;
  border-right: 1px solid ${borderColor};

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 1rem 1.5rem;
  };
`;

export const OperationModeContainer = styled.div`
  width: 100%;
  padding: 0.75rem 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 9px;
  justify-content: center;
  align-items: center;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 1rem 1.5rem;
  };

  @media (min-width: ${BREAKPOINTS.md}px) {
    padding: 1rem 1.5rem;
  };
`;

export const FooterContainer = styled.div`
  width: 100%;
  ${flexBetween};
  border-radius: 4px;
  padding: 0.75rem 1rem;
  margin-top: 0.75rem;
  background-color: ${({
    theme,
  }) => theme.color.crusherDashboard.hydraulicUnitDrawer.hydraulicFooterBackgroundColor};

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 1rem 1.5rem;
    margin-top: 1rem;
  };

  @media (min-width: ${BREAKPOINTS.md}px) {
    padding: 1rem 1.5rem;
    margin-top: 1.25rem;
  };
`;

export const OverallContainer = styled.div`
  width: 100%;
  border: 1px solid ${borderColor};
  border-radius: 4px;
`;

export const PermissiveContainer = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;

  @media (min-width: ${BREAKPOINTS.xl}px) {
    padding-bottom: 0;
    padding-top: 1rem;
    padding-bottom: 1rem;
  };
`;

export const PermissiveTitleContainer = styled.div`
  padding: 10px 20px;
  width: 100%;
`;

export const PermissiveList = styled.ul`
  padding: 0 1rem 1.5rem 2rem;
  margin: 0;
  flex: 1;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 0 1.25rem 2rem 2rem;
  };
`;

export const PermissiveListFirst = styled(PermissiveList)`
  padding-bottom: 0;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding-bottom: 0;
  };

  @media (min-width: ${BREAKPOINTS.xl}px) {
    padding: 0 0.5rem 2rem 2rem;
  };
`;

export const PermissiveListSecond = styled(PermissiveList)`
  padding-top: 0;
  margin-top: 0;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding-top: 0;
  };

  @media (min-width: ${BREAKPOINTS.xl}px) {
    padding: 0 1.25rem 2rem 0.5rem;
    margin-top: 0;
  };
`;

export const PermissiveItem = styled.li<{ status: StatusIndicatorStatus }>`
  list-style-type: none;
  padding: 12px 20px 12px 14px;
  background-color: ${({ status, theme }) => (
    status === StatusIndicatorStatus.alarm
      ? theme.color.crusherDashboard.hydraulicUnitDrawer.descriptionListErrorBackgroundColor
      : 'transparent'
  )};
  border-radius: 4px;
`;

export const BorderBottom = styled.div`
  border-bottom: 1px solid ${borderColor};
`;

export const CSSValueContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
`;

export const ParagraphUnitContainer = styled.div`
  padding-bottom: 1px;
`;
